import React, { useEffect, useRef, useState } from 'react'
import {
  MenuOutlined,
  MoreOutlined,
  PaperClipOutlined,
  PhoneOutlined,
  SearchOutlined,
  SendOutlined,
  LoadingOutlined,
  VideoCameraOutlined
} from '@ant-design/icons';
import { InView } from 'react-intersection-observer';
import { Avatar, Button, Dropdown, Input, MenuProps, Spin } from 'antd';
import { AppDispatch, RootState } from '../../../redux/store/store';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMensajesConversaciones, setConversacionSeleccionadaAction, setNuevoMensajeConversacionAction, updateMensajeConversacionAction } from '../../../redux/actions/chat/conversaciones/Conversaciones';
import { socket } from '../../../utils/services/socket';
import InfiniteScroll from 'react-infinite-scroll-component';
import ChatBubble from './ChatBubble';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { formatRelativeDateTimezone } from '../../../utils/formatTime/FormatTimeZone';
dayjs.extend(utc);
dayjs.extend(timezone);

const contactActions: MenuProps['items'] = [
  { key: '1', label: 'View Contact' },
  { key: '2', label: 'Mute Notifications' },
  { key: '3', label: 'Block Contact' },
  { key: '4', label: 'Clear Chat' },
  { key: '5', label: 'Report' },
];

const Conversacion = () => {

  const dispatch = useDispatch<AppDispatch>();

  const {
    conversacion_seleccionada,
    mensajes_conversacion,
    paginationMensajes
  } = useSelector((state: RootState) => state.conversaciones);

  const { rex_user } = useSelector((state: RootState) => state.login);

  const [message, setMessage] = useState('')
  const divRef = useRef(null);
  const [hasScroll, setHasScroll] = useState(false);

  const [topVisibleDate, setTopVisibleDate] = useState<string | null>(null);
  const visiblePeriodsRef = useRef<Record<string, IntersectionObserverEntry>>({});

  const handlePeriodVisibility = (dateKey: string, inView: boolean, entry: IntersectionObserverEntry) => {
    if (inView) {
      visiblePeriodsRef.current[dateKey] = entry;
    } else {
      delete visiblePeriodsRef.current[dateKey];
    }

    const visible = Object.entries(visiblePeriodsRef.current);
    if (visible.length > 0) {
      const topMost = visible.reduce((prev, curr) =>
        prev[1].boundingClientRect.top < curr[1].boundingClientRect.top ? prev : curr
      );

      const samePeriod = groupMessages(mensajes_conversacion)

      if (!samePeriod.find(prd => formatRelativeDateTimezone(prd.date) == topMost[0])) {
        return false
      }

      if (samePeriod.length == 1) {
        if (formatRelativeDateTimezone(samePeriod[0]['date']) != topMost[0]) {
          return false
        }
      }

      setTopVisibleDate(topMost[0])
    }
  };

  const getData = async (reloadAll = false) => {
    if (conversacion_seleccionada?.id) {
      await dispatch(fetchMensajesConversaciones(
        conversacion_seleccionada.id,
        reloadAll ? 0 : mensajes_conversacion?.length,
        paginationMensajes.limit,
      ))
    }
  }

  const groupMessages = (
    messages: any,
    timezone: string = 'America/Lima'
  ): Array<{ date: string; messages: Array<Array<typeof messages[number]>> }> => {

    return messages.reduce((acc: any, message: any) => {
      const messageDate = dayjs(message.createdAt).tz(timezone).format('YYYY-MM-DD');
      const lastDayGroup = acc[acc.length - 1];

      if (!lastDayGroup || lastDayGroup.date !== messageDate) {
        acc.push({ date: messageDate, messages: [[message]] });
      } else {
        const lastGroup = lastDayGroup.messages[lastDayGroup.messages.length - 1];
        const prevMessage = lastGroup[lastGroup.length - 1];
        const diffInMinutes = dayjs(message.createdAt).diff(dayjs(prevMessage?.createdAt), 'minute');

        if (
          prevMessage &&
          message.emisorId === prevMessage.emisorId
          && diffInMinutes === 0
        ) {
          lastGroup.push(message);
        } else {
          lastDayGroup.messages.push([message]);
        }
      }

      return acc;
    }, [] as Array<{ date: string; messages: Array<Array<typeof messages[number]>> }>);
  }

  const sendMessage = () => {

    if (!conversacion_seleccionada) return

    if (conversacion_seleccionada.id) {
      socket.emit('sendMessage', {
        message: message,
        userId: rex_user.id,
        conversationId: conversacion_seleccionada.id
      }, (response: boolean) => {
        if (response) setMessage('')
      })
    } else {
      socket.emit('newConversation', {
        message: message,
        userId: rex_user.id,
        messageTo: conversacion_seleccionada.user.id
      }, (response: boolean) => {
        if (response) {
          setMessage('')
          dispatch(setConversacionSeleccionadaAction(response))
        }
      })
    }
  }

  useEffect(() => {
    setTopVisibleDate('')
    if (conversacion_seleccionada?.id) {
      socket.emit('joinConversation', {
        conversationId: conversacion_seleccionada.id
      })
    }

    socket.on('newMessageEvent', (msg) => {
      dispatch(setNuevoMensajeConversacionAction(msg))
    });

    socket.on('messageMarkedAsReadEvent', (msg) => {
      dispatch(updateMensajeConversacionAction(msg))
    });

    return () => {
      socket.off('newMessageEvent');
    };
  }, [conversacion_seleccionada]);

  useEffect(() => {
    getData(true)
    setTopVisibleDate('')

  }, [conversacion_seleccionada?.id])

  useEffect(() => {
    const div: any = divRef.current;
    if (div) {
      const isScrollable = div.scrollHeight > div.clientHeight || div.scrollWidth > div.clientWidth;
      setHasScroll(isScrollable);
    }
  }, [mensajes_conversacion]);

  return (
    <div className="flex justify-between flex-col h-[100%] relative">
      {/* Chat Header */}

      <div className="p-4 border-b h-[65px] flex justify-between items-center">
        <div className="flex items-center">
          <MenuOutlined className="mr-3 md:hidden" />
          <Avatar src={conversacion_seleccionada?.user.imagen_perfil} className="mr-3" />
          <div>
            <h6 className="font-normal">
              {conversacion_seleccionada?.user.personas.nombre + ' ' + conversacion_seleccionada?.user.personas.apellidos}
            </h6>
            <small className="text-gray-500">{'Offline'}</small>
          </div>
        </div>
        <div className="flex items-center">
          {/* <Button type="text" icon={<PhoneOutlined />} /> */}
          {/* <Button type="text" icon={<VideoCameraOutlined />} /> */}
          <Button type="text" icon={<SearchOutlined />} />
          {/* <Dropdown menu={{ items: contactActions }} trigger={['click']}>
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown> */}
        </div>
      </div>

      {/* Messages CLINICA*/}
      <div
        id="scrollableDiv"
        ref={divRef}
        style={{
          height: '100%',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column-reverse',
          paddingLeft: '8px',
          paddingRight: '4px',
          paddingTop: '10px',
        }}

      >
        <InfiniteScroll
          dataLength={mensajes_conversacion?.length ?? 0}
          next={getData}
          style={{
            display: 'flex',
            flexDirection: 'column-reverse',
            height: '100%',
          }}

          inverse={true}
          hasMore={(mensajes_conversacion?.length ?? 0) < paginationMensajes.total}
          loader={
            <div className='w-full mt-2 flex justify-center'>
              <Spin size="large" indicator={<LoadingOutlined spin />} />
            </div>
          }
          scrollableTarget="scrollableDiv"
        >
          {
            topVisibleDate != '' && hasScroll
              ? <div
                className="absolute top-[75px] left-1/2 transform text-center py-[1px] px-2 rounded-md bg-blue-400 text-white"
                style={{ transform: 'translateX(calc(-50% - 5px))' }}
              >
                {topVisibleDate}
              </div>
              : null
          }

          {
            mensajes_conversacion
            && groupMessages(mensajes_conversacion).map((period: any, index: number) => (
              <div key={index}>
                <InView
                  as="div"
                  threshold={0}
                  root={document.getElementById('scrollableDiv') || undefined}
                  onChange={(inView, entry) => {
                    handlePeriodVisibility(formatRelativeDateTimezone(period.date), inView, entry)
                  }}
                >
                  <div className='flex justify-center'>
                    <span className='py-[1px] px-2 rounded-md bg-blue-400 text-white z-10'>
                      {formatRelativeDateTimezone(period.date)}
                    </span>
                  </div>
                  {period.messages.reverse().map((group: any, i: number) => {
                    const messageSelf = rex_user?.id === group[0]['emisorId'];

                    return (
                      <div key={i} className={`mb-2 flex ${messageSelf ? 'justify-end' : 'justify-start'}`}>
                        <ChatBubble
                          messageSelf={messageSelf}
                          messages={group}
                          userId={rex_user?.id}
                        />
                      </div>
                    );
                  })}
                </InView>
              </div>
            ))
          }
        </InfiniteScroll>
      </div>
      {/* Message Input */}
      <div className="p-4 border-t h-[70px]">
        <div className="flex items-center">
          <Input
            placeholder="Type your message here..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onPressEnter={sendMessage}
            className="flex-grow mr-2"
          />
          <Button type="text" icon={<PaperClipOutlined />} />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={sendMessage}
          >
            <span className="hidden md:inline">Send</span>
          </Button>
        </div>
      </div>
    </div>
  )
}

export default Conversacion